import React from 'react';
import { useCurrentUserWorkspace } from '@mentimeter/workspace-data-hooks';
import { useTranslations } from 'next-intl';
import { useIsRoleUpgradeEnabled } from '../hooks/use-role-upgrade-enabled';
import { useClosePopoverPaywall } from '../hooks/use-popover-paywall';
import { useTrackOpenPaywall } from '../tracking/track-open-paywall';
import { UpgradeType } from '../../types';
import { RequestUpgradePopoverContentGeneric } from './popover/RequestUpgradePopoverContentGeneric';
import type { RequestUpgradeComponentProps } from './types';
import { useRequestUpgradeMutation } from './useRequestUpgradeMutation';
import { RoleUpgradeDisabledPopoverContentNew } from './popover/RoleUpgradeDisabledPopoverContentNew';
import { RoleUpgradeDisabledDataHandler } from './RoleUpgradeDisabledDataHandler';

export function RequestUpgradePopoverGeneric({
  title,
  description,
  trackingContext,
  trackingPlacement,
}: Omit<RequestUpgradeComponentProps, 'children'>) {
  const closePopover = useClosePopoverPaywall();
  const { data: workspace } = useCurrentUserWorkspace();
  const isRoleUpgradeEnabled = useIsRoleUpgradeEnabled();
  const requestUpgradeMutation = useRequestUpgradeMutation({
    trackingPlacement,
    trackingContext,
    onSuccess: closePopover,
  });
  const t = useTranslations('purchasing.request_upgrade');

  useTrackOpenPaywall({
    trackingContext,
    trackingPlacement,
    upgradeType: UpgradeType.REQUEST_UPGRADE,
  });

  if (!isRoleUpgradeEnabled) {
    return (
      <RoleUpgradeDisabledDataHandler workspaceId={workspace?.id}>
        {({ message }) => (
          <RoleUpgradeDisabledPopoverContentNew
            title={title ?? t('default_title')}
            description={description ?? t('default_description')}
            message={message}
            onDismissClick={closePopover}
          />
        )}
      </RoleUpgradeDisabledDataHandler>
    );
  }

  return (
    <RequestUpgradePopoverContentGeneric
      title={title ?? t('default_title')}
      description={description ?? t('default_description')}
      onDismissClick={closePopover}
      onRequestUpgrade={requestUpgradeMutation.trigger}
    />
  );
}
